import { Dialog, Button } from '@mui/material';
import React, { useEffect, useState } from 'react'
import DOMPurify from 'dompurify';
import { useTheme } from '../../../components/hooks/useTheme';
import colorConfigs from '../../../configs/colorConfigs';

type Props = {
    particularAudit: any;
    showPopup: boolean;
    hidePopup: () => void;
}

const AuditDetailsPopup = ({ particularAudit, showPopup, hidePopup }: Props) => {
    const { themeStyles } = useTheme();
    const [oldValue, setOldValue] = useState<any>("");
    const [newValue, setNewValue] = useState<any>("");

    useEffect(() => {
        if (showPopup) {
            const oldStr = JSON.stringify(particularAudit?.formattedOldValue, undefined, 2)
            const newStr = JSON.stringify(particularAudit?.formattedNewValue, undefined, 2)

            getStrDiff(oldStr, newStr)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPopup])

    const getStrDiff = (oldStr: string, newStr: string) => {
        let oldStrResult = "";
        let newStrResult = "";

        if (oldStr !== 'null' && newStr !== 'null') {
            const oldJson = JSON.parse(oldStr);
            const newJson = JSON.parse(newStr);

            Object?.keys(oldJson)?.forEach((key, ind) => {
                if (key !== Object.keys(newJson)[ind]) {
                    if (ind === 0) {
                        oldStrResult += `<span style="background-color: #f76b6b; padding: 0 3px;">{<br/> ${key}</span>:`;
                        newStrResult += `<span style="background-color: #80d683; padding: 0 3px;">{<br/> ${Object.keys(newJson)[ind]}</span>:`;
                    } else {
                        oldStrResult += `<span style="background-color: #f76b6b; padding: 0 3px;">${key}</span>:`;
                        newStrResult += `<span style="background-color: #80d683; padding: 0 3px;">${Object.keys(newJson)[ind]}</span>:`;
                    }
                } else {
                    if (ind === 0) {
                        oldStrResult += `{<br/>${key}:`;
                        newStrResult += `{<br/>${key}:`;
                    } else {
                        oldStrResult += `${key}:`;
                        newStrResult += `${key}:`;
                    }
                }

                if (oldJson[key] !== Object.values(newJson)[ind]) {
                    oldStrResult += `<span style="background-color: #f76b6b; padding: 0 3px;">${oldJson[key]}</span>,<br/>`
                    newStrResult += `<span style="background-color: #80d683; padding: 0 3px;">${Object.values(newJson)[ind]}</span>,<br/>`
                } else {
                    oldStrResult += `${oldJson[key]},<br/>`
                    newStrResult += `${Object.values(newJson)[ind]},<br/>`
                }
            })

            oldStrResult += '}'
            newStrResult += '}'
        } else {
            oldStrResult = `<span style="background-color: #f76b6b; padding: 0 3px;">${oldStr}</span>`;

            newStrResult = `<span style="background-color: #80d683; padding: 0 3px;">${newStr}</span>`;
        }

        setOldValue(() => DOMPurify.sanitize(oldStrResult, {
            USE_PROFILES: { html: true },
        }));

        setNewValue(() => DOMPurify.sanitize(newStrResult, {
            USE_PROFILES: { html: true },
        }));
    }

    return (
        <>
            <Dialog
                open={showPopup}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '800px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[24px] py-[10px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                    <div className="flex items-center gap-1.5">
                                        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Audit Details</h2>
                                    </div>

                                    <button
                                        onClick={hidePopup}
                                        className="absolute right-2 top-2 text-gray-500 hover:text-red-500 transition"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">close</span>
                                    </button>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <div className="grid grid-cols-2 gap-6">
                                            {/* First Column */}
                                            <div className="space-y-4">
                                                <div className="flex flex-col gap-1.5">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Audit User</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularAudit?.auditUser}</span>
                                                </div>

                                                <div className="flex flex-col gap-1.5">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Table Name</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularAudit?.tableName}</span>
                                                </div>

                                                <div className="flex flex-col gap-1.5">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Event Count</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularAudit?.keyValues}</span>
                                                </div>

                                                <div className="flex flex-col gap-1.5">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Path</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor} break-words whitespace-pre-wrap w-full`}>{particularAudit?.changedColumns}</span>
                                                </div>

                                                <div className="flex flex-col gap-1.5">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Old Value</span>
                                                    <span
                                                        className={`text-sm ${themeStyles.main.textSubHeadingColor} break-words whitespace-pre-wrap w-full`}
                                                        dangerouslySetInnerHTML={{ __html: oldValue }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Second Column */}
                                            <div className="space-y-4">
                                                <div className="flex flex-col gap-1.5">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Audit Type</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularAudit?.auditType}</span>
                                                </div>

                                                <div className="flex flex-col gap-1.5">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Date</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularAudit?.auditDateTimeUtc}</span>
                                                </div>

                                                <div className="flex flex-col gap-1.5 invisible">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Date</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularAudit?.auditDateTimeUtc}</span>
                                                </div>

                                                <div className="flex flex-col gap-1.5 invisible">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>Date</span>
                                                    <span className={`text-sm ${themeStyles.main.textSubHeadingColor}`}>{particularAudit?.auditDateTimeUtc}</span>
                                                </div>

                                                <div className="flex flex-col gap-1.5">
                                                    <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>New Value</span>
                                                    <span
                                                        className={`text-sm ${themeStyles.main.textSubHeadingColor} break-words whitespace-pre-wrap w-full`}
                                                        dangerouslySetInnerHTML={{ __html: newValue }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Buttons */}
                                        <div className="flex gap-4 mt-6">
                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.cancel.bg,
                                                    border: colorConfigs.btn.cancel.border,
                                                    color: colorConfigs.btn.cancel.color
                                                }}
                                                onClick={hidePopup}
                                            >
                                                Close
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    )
}

export default AuditDetailsPopup